// ============================================
// Dr. C Review Queue
//
// Simple list of cases awaiting Dr. C's review.
// Click opens the review card.
// ============================================

import { useState, useEffect } from 'react';
import { ClipboardCheck, User, FileText } from 'lucide-react';
import type { CalibrationCase } from '../../../types/labUpload';
import { getCalibrationCases } from '../../../services/firestore/calibrationCaseService';
import { DrCReview } from './DrCReview';

interface DrCReviewQueueProps {
  brandId: string;
}

export function DrCReviewQueue({ brandId }: DrCReviewQueueProps) {
  const [cases, setCases] = useState<CalibrationCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCase, setActiveCase] = useState<CalibrationCase | null>(null);

  async function loadCases() {
    setLoading(true);
    try {
      const compared = await getCalibrationCases(brandId, { status: 'compared' });
      setCases(compared);
    } catch (err) {
      console.error('[DrCReviewQueue] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadCases(); }, [brandId]);

  if (activeCase) {
    return (
      <div>
        <button
          onClick={() => setActiveCase(null)}
          className="text-sm text-zinc-500 hover:text-zinc-700 mb-3"
        >
          &larr; Back to queue
        </button>
        <DrCReview
          calibrationCase={activeCase}
          onReviewComplete={() => {
            setActiveCase(null);
            loadCases();
          }}
        />
      </div>
    );
  }

  if (loading) {
    return <p className="text-sm text-zinc-500 py-4">Loading review queue...</p>;
  }

  if (cases.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <ClipboardCheck size={32} className="mx-auto mb-2" />
        <p className="text-sm">No cases awaiting review</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-zinc-600 mb-3">
        {cases.length} case{cases.length !== 1 ? 's' : ''} awaiting review
      </p>

      {cases.map((c) => (
        <button
          key={c.caseId}
          onClick={() => setActiveCase(c)}
          className="w-full text-left bg-white border border-zinc-200 rounded p-4 hover:border-rose-300 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText size={16} className="text-zinc-500" />
              <span className="text-sm font-medium text-zinc-800">
                Case #{c.caseId.slice(-6)}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {c.demographics && (
                <span className="text-[13px] text-zinc-500 flex items-center gap-1">
                  <User size={12} />
                  {c.demographics.sex ?? '?'}, {c.demographics.age ?? '?'}y
                </span>
              )}

              {c.alignmentScore !== undefined && (
                <span className={`text-[13px] font-mono px-2 py-0.5 rounded ${
                  c.alignmentScore >= 75 ? 'bg-green-100 text-green-700' :
                  c.alignmentScore >= 50 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {c.alignmentScore}%
                </span>
              )}
            </div>
          </div>

          {c.blindRunOutput && (
            <p className="text-[13px] text-zinc-500 mt-1 truncate">
              {c.blindRunOutput.diagnosis}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}
